/**
 * TBMuktBharat Competition Submit Page
 * Form for students to submit their competition entries
 */

import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { GovLayout, GovPageTitle, GovCard, GovButton } from '@/components/layout/GovLayout'
import {
  ArrowLeft, Upload, FileText, AlertCircle, CheckCircle, Loader2,
  User, Mail, Phone, Building, MapPin, GraduationCap
} from 'lucide-react'
import { motion } from 'framer-motion'
import competitionService, { Competition, CompetitionSubmitRequest } from '@/services/competitionService'
import { useAuthStore } from '@/stores/authStore'
import api, { getApiErrorMessage, UPLOAD_TIMEOUT } from '@/services/api'

// ================================================================================
// ALLOWED FILE TYPES
// ================================================================================
const FILE_TYPE_CONFIG: Record<string, { maxSize: number; accept: string }> = {
  PDF: { maxSize: 10 * 1024 * 1024, accept: '.pdf' },
  DOC: { maxSize: 10 * 1024 * 1024, accept: '.doc,.docx' },
  DOCX: { maxSize: 10 * 1024 * 1024, accept: '.doc,.docx' },
  JPG: { maxSize: 10 * 1024 * 1024, accept: '.jpg,.jpeg' },
  PNG: { maxSize: 10 * 1024 * 1024, accept: '.png' },
}

// ================================================================================
// MAIN COMPONENT
// ================================================================================

const CompetitionSubmitPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()

  const [competition, setCompetition] = useState<Competition | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Form state
  const [formData, setFormData] = useState<CompetitionSubmitRequest>({
    fullName: '',
    mobileNumber: '',
    email: '',
    schoolCollegeName: '',
    classStandard: '',
    district: '',
    state: '',
    submissionTitle: '',
    submissionDescription: '',
    fileUrl: '',
    fileType: '',
    fileSize: 0,
    fileName: '',
    agreeToTerms: false,
  })

  // File state
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/competitions/${id}/submit` } })
      return
    }

    if (id) {
      fetchCompetition()
      prefillUserData()
    }
  }, [id, isAuthenticated])

  const fetchCompetition = async () => {
    setLoading(true)
    try {
      const data = await competitionService.getCompetitionById(Number(id))

      // Check if competition is still active
      if (data.status !== 'ACTIVE' || data.isExpired) {
        setError('This competition is no longer accepting submissions.')
      }

      // Check if already submitted
      if (data.hasSubmitted) {
        setError('You have already submitted an entry to this competition.')
      }

      setCompetition(data)
    } catch (err) {
      setError('Failed to load competition details.')
    } finally {
      setLoading(false)
    }
  }

  const prefillUserData = () => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: user.email || '',
      }))
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    const extension = file.name.split('.').pop()?.toUpperCase() || ''
    const allowedTypes = competition?.allowedFileTypes?.split(',') || ['PDF', 'DOC', 'DOCX', 'JPG', 'PNG']

    if (!allowedTypes.includes(extension)) {
      setError(`Invalid file type. Allowed types: ${allowedTypes.join(', ')}`)
      return
    }

    // Validate file size
    const maxSize = competition?.maxFileSize || 10 * 1024 * 1024
    if (file.size > maxSize) {
      setError(`File too large. Maximum size: ${(maxSize / 1024 / 1024).toFixed(0)}MB`)
      return
    }

    setSelectedFile(file)
    setUploading(true)
    setError(null)

    try {
      // Upload file
      const formDataUpload = new FormData()
      formDataUpload.append('file', file)
      formDataUpload.append('mediaType', 'DOCUMENT')

      const response = await api.post('/media/upload', formDataUpload, {
        timeout: UPLOAD_TIMEOUT,
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      const { url, fileName: uploadedFileName, fileSize } = response.data.data

      setFormData(prev => ({
        ...prev,
        fileUrl: url,
        fileType: extension,
        fileSize: file.size,
        fileName: file.name,
      }))

      // Create preview for images
      if (['JPG', 'JPEG', 'PNG'].includes(extension)) {
        const reader = new FileReader()
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string)
        }
        reader.readAsDataURL(file)
      } else {
        setPreviewUrl(null)
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to upload file. Please try again.'))
      setSelectedFile(null)
    } finally {
      setUploading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate
    if (!formData.fullName.trim()) {
      setError('Please enter your full name.')
      return
    }

    if (!formData.schoolCollegeName?.trim()) {
      setError('Please enter your school/college name.')
      return
    }

    if (!formData.fileUrl) {
      setError('Please upload your submission file.')
      return
    }

    if (!formData.agreeToTerms) {
      setError('Please agree to the terms and conditions.')
      return
    }

    setSubmitting(true)

    try {
      await competitionService.submitEntry(Number(id), formData)
      setSuccess(true)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <GovLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </GovLayout>
    )
  }

  if (success) {
    return (
      <GovLayout>
        <div className="max-w-2xl mx-auto">
          <GovCard className="text-center py-12">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Submission Successful!</h2>
            <p className="text-gray-600 mb-6">
              Your entry has been submitted successfully. Our team will review it and announce results soon.
            </p>
            <div className="flex justify-center gap-4">
              <Link
                to="/competitions"
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Browse Competitions
              </Link>
              <Link
                to="/competitions/my-submissions"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                View My Submissions
              </Link>
            </div>
          </GovCard>
        </div>
      </GovLayout>
    )
  }

  return (
    <GovLayout>
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <Link
          to={`/competitions/${id}`}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Competition</span>
        </Link>

        <GovPageTitle
          title="Submit Your Entry"
          subtitle={competition?.title}
        />

        {/* Competition Info */}
        {competition && (
          <GovCard className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">
                {competition.type === 'POSTER' ? '🎨' :
                 competition.type === 'QUIZ' ? '🧠' :
                 competition.type === 'ESSAY' ? '📝' :
                 competition.type === 'DRAWING' ? '🖌️' : '💬'}
              </span>
              <span className="font-medium text-gray-900">{competition.title}</span>
            </div>
            <div className="text-sm text-gray-500">
              Deadline: {competition.endDate ? new Date(competition.endDate).toLocaleDateString() : 'N/A'}
              {' • '}{competition.currentParticipants || 0} participants
            </div>
          </GovCard>
        )}

        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {/* Submission Form */}
        <form onSubmit={handleSubmit}>
          <GovCard className="mb-4">
            <h2 className="font-bold text-gray-900 mb-4">Personal Information</h2>

            <div className="space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
              </div>

              {/* Mobile & Email */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mobile Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      name="mobileNumber"
                      value={formData.mobileNumber}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+91..."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
              </div>

              {/* School/College */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School/College Name *
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="schoolCollegeName"
                    value={formData.schoolCollegeName}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your school or college name"
                    required
                  />
                </div>
              </div>

              {/* Class/Standard */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Class/Standard
                </label>
                <div className="relative">
                  <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="classStandard"
                    value={formData.classStandard}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 10th, 12th, Graduate"
                  />
                </div>
              </div>

              {/* District & State */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    District
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      name="district"
                      value={formData.district}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="District"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State *
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select State</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Delhi">Delhi</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Gujarat">Gujarat</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    <option value="West Bengal">West Bengal</option>
                    <option value="Rajasthan">Rajasthan</option>
                    <option value="Madhya Pradesh">Madhya Pradesh</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          </GovCard>

          {/* Submission Details */}
          <GovCard className="mb-4">
            <h2 className="font-bold text-gray-900 mb-4">Submission Details</h2>

            <div className="space-y-4">
              {/* Submission Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title of Your Entry
                </label>
                <input
                  type="text"
                  name="submissionTitle"
                  value={formData.submissionTitle}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Give your submission a title"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="submissionDescription"
                  value={formData.submissionDescription}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe your submission..."
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Your Entry *
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {uploading ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-2" />
                      <p className="text-sm text-gray-500">Uploading...</p>
                    </div>
                  ) : selectedFile ? (
                    <div className="flex flex-col items-center">
                      {previewUrl ? (
                        <img src={previewUrl} alt="Preview" className="max-h-40 rounded-lg mb-2" />
                      ) : (
                        <FileText className="w-12 h-12 text-gray-400 mb-2" />
                      )}
                      <p className="text-sm font-medium">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFile(null)
                          setFormData(prev => ({ ...prev, fileUrl: '', fileName: '' }))
                          setPreviewUrl(null)
                        }}
                        className="mt-2 text-sm text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 mb-1">
                        Drag and drop your file here, or click to browse
                      </p>
                      <p className="text-xs text-gray-500">
                        Allowed: {competition?.allowedFileTypes || 'PDF, DOC, DOCX, JPG, PNG'} • Max: {(competition?.maxFileSize || 10485760) / 1024 / 1024}MB
                      </p>
                      <input
                        type="file"
                        accept={competition?.allowedFileTypes?.split(',').map(t => FILE_TYPE_CONFIG[t.trim()]?.accept).join(',') || '.pdf,.doc,.docx,.jpg,.jpeg,.png'}
                        onChange={handleFileChange}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        style={{ position: 'relative' }}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </GovCard>

          {/* Terms */}
          <GovCard className="mb-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.agreeToTerms}
                onChange={e => setFormData(prev => ({ ...prev, agreeToTerms: e.target.checked }))}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">
                I declare that this submission is my original work. I agree to the competition rules
                and grant permission for my submission to be displayed on the platform.
              </span>
            </label>
          </GovCard>

          {/* Submit Button */}
          <div className="flex gap-4">
            <Link
              to={`/competitions/${id}`}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-center hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting || uploading}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Entry'
              )}
            </button>
          </div>
        </form>
      </div>
    </GovLayout>
  )
}

export default CompetitionSubmitPage